/**
 * Photo and document upload.
 *
 * The bytes go straight from the browser to object storage — see `uploadDocument`
 * in lib/dataProvider.ts and lib/storage.ts on the server. This component owns the
 * three-step dance's user-visible half: pick a file, watch it go, see it fail
 * legibly if it does.
 *
 * One file at a time, deliberately. A multi-file picker looks friendlier and then
 * has to answer "three of five uploaded, what now" — partial success with no
 * transaction. Sequential single uploads make each outcome unambiguous, and staff
 * uploading a set of inspection photos are already going one at a time mentally.
 */

import { useRef, useState } from 'react';
import { DOCUMENT_TYPES, DOCUMENT_VISIBILITIES } from 'shared';
import { uploadDocument, type UploadedDocument } from '../lib/dataProvider.js';
import { Field, Select, humanizeEnum } from './Form.js';

/** Mirrors ALLOWED_CONTENT_TYPES in the API's lib/storage.ts. */
const ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,application/pdf';

/** Same allowlist minus `application/pdf`, for contexts where only a photo makes sense. */
const IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp,image/heic';

/** Mirrors MAX_UPLOAD_BYTES. Checked here only to fail fast before a round trip. */
const MAX_BYTES = 25 * 1024 * 1024;

interface PhotoUploadProps {
  /** Exactly one parent, matching the API's single-parent constraint. */
  parent:
    | { propertyId: string }
    | { unitId: string }
    | { leaseId: string }
    | { tenantId: string }
    | { workOrderId: string }
    | { ownerId: string };
  defaultDocumentType?: string;
  onUploaded: (document: UploadedDocument) => void;
  /**
   * Hides the "Document type" / "Visible to" pickers, using
   * `defaultDocumentType` and the default 'STAFF_ONLY' visibility as fixed
   * values. For an audience the staff-facing enum (`PROPERTY_PHOTO`,
   * `OWNER_PHOTO`, …) and the `visibility` field mean nothing to — a tenant
   * uploading their own maintenance photo, say.
   */
  simple?: boolean;
  /**
   * Restricts the accepted types to actual photo formats (drops
   * `application/pdf`) and swaps the single file input for two buttons — one
   * that opens the device camera directly (`capture="environment"`), one
   * that opens the regular file/gallery picker. A PDF scan of an inspection
   * report is a legitimate upload elsewhere in this component; a tenant
   * photographing a leaking faucet is never going to attach one.
   */
  imageOnly?: boolean;
}

export function PhotoUpload({
  parent,
  defaultDocumentType,
  onUploaded,
  simple,
  imageOnly,
}: PhotoUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [documentType, setDocumentType] = useState(defaultDocumentType ?? 'PROPERTY_PHOTO');
  const [visibility, setVisibility] = useState('STAFF_ONLY');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accept = imageOnly ? IMAGE_ACCEPT : ACCEPT;

  async function handleFile(file: File) {
    setError(null);

    // Checked client-side purely to avoid a pointless round trip. The API validates
    // both again — this is a convenience, not the enforcement point.
    if (file.size > MAX_BYTES) {
      setError(`That file is ${formatBytes(file.size)}. The limit is ${formatBytes(MAX_BYTES)}.`);
      return;
    }
    if (!accept.split(',').includes(file.type)) {
      setError(
        imageOnly
          ? `${file.type || 'That file type'} is not accepted. Use a JPEG, PNG, WebP, or HEIC photo.`
          : `${file.type || 'That file type'} is not accepted. Use a JPEG, PNG, WebP, HEIC or PDF.`,
      );
      return;
    }

    setBusy(true);
    try {
      const uploaded = await uploadDocument(file, { ...parent, documentType, visibility });
      onUploaded(uploaded);
      // Cleared so the same file can be picked again after a failure-then-retry.
      // Without this, selecting the identical file fires no change event.
      if (inputRef.current) inputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The upload failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      {simple ? null : (
        <>
          <Field label="Document type" name="documentType">
            <Select
              name="documentType"
              value={documentType}
              onChange={setDocumentType}
              options={DOCUMENT_TYPES}
            />
          </Field>

          <Field
            label="Visible to"
            name="visibility"
            hint="Staff can see everything by default. This option controls visibility in the portals."
          >
            <Select
              name="visibility"
              value={visibility}
              onChange={setVisibility}
              options={DOCUMENT_VISIBILITIES}
            />
          </Field>
        </>
      )}

      {imageOnly ? (
        <Field label="Photo" name="file" hint="JPEG, PNG, WebP, or HEIC · up to 25MB">
          <div className="row">
            <input
              ref={cameraInputRef}
              id="file-camera"
              name="file-camera"
              type="file"
              accept={accept}
              capture="environment"
              disabled={busy}
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void handleFile(file);
              }}
            />
            <button
              type="button"
              className="button button--quiet"
              disabled={busy}
              onClick={() => cameraInputRef.current?.click()}
            >
              Take photo
            </button>
            <input
              ref={inputRef}
              id="file"
              name="file"
              type="file"
              accept={accept}
              disabled={busy}
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void handleFile(file);
              }}
            />
            <button
              type="button"
              className="button button--quiet"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
            >
              Upload photo
            </button>
          </div>
        </Field>
      ) : (
        <Field label="File" name="file" hint="JPEG, PNG, WebP, HEIC or PDF · up to 25MB">
          <input
            ref={inputRef}
            id="file"
            name="file"
            className="field__input"
            type="file"
            accept={accept}
            disabled={busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void handleFile(file);
            }}
          />
        </Field>
      )}

      {busy ? <p className="muted">Uploading…</p> : null}
      {error ? <p className="alert alert--error">{error}</p> : null}
    </div>
  );
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Human label for a document type, reused by the listings. */
export function documentTypeLabel(value: string): string {
  return humanizeEnum(value);
}
